import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faSearch,
  faDownload,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "@mui/material";
import { toast } from "react-hot-toast";

interface ExcelSerialRow {
  materialName: string;
  serial: string;
  normalizedSerial: string;
  productId: string;
  point: string;
  sourceRow: number;
}

interface CommonSerialRow {
  serial: string;
  normalizedSerial: string;
  companyMaterialName: string;
  agentMaterialName: string;
  productId: string;
  point: string;
  companyRow: number;
  agentRow: number;
}

interface AgentOnlySerialRow {
  serial: string;
  normalizedSerial: string;
  agentMaterialName: string;
  productId: string;
  point: string;
  agentRow: number;
}

type ActiveTab = "common" | "agentOnly";

const SerialComparison: React.FC = () => {
  const [companyFileName, setCompanyFileName] = useState("");
  const [agentFileName, setAgentFileName] = useState("");

  const [companyRows, setCompanyRows] = useState<ExcelSerialRow[]>([]);
  const [agentRows, setAgentRows] = useState<ExcelSerialRow[]>([]);

  const [companyDuplicatesCount, setCompanyDuplicatesCount] = useState(0);
  const [agentDuplicatesCount, setAgentDuplicatesCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<ActiveTab>("common");

  const normalizeDigits = (value: string) => {
    const arabicDigits = "٠١٢٣٤٥٦٧٨٩";
    const persianDigits = "۰۱۲۳۴۵۶۷۸۹";

    return value.replace(/[٠-٩۰-۹]/g, (digit) => {
      const arabicIndex = arabicDigits.indexOf(digit);
      if (arabicIndex > -1) return String(arabicIndex);

      const persianIndex = persianDigits.indexOf(digit);
      if (persianIndex > -1) return String(persianIndex);

      return digit;
    });
  };

  const normalizeSerial = (value: any) => {
    return normalizeDigits(String(value ?? ""))
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, "")
      .replace(/[-–—]/g, "-")
      .trim()
      .toUpperCase();
  };

  const normalizeHeader = (value: string) => {
    return String(value ?? "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, "")
      .replace(/_/g, "")
      .trim()
      .toLowerCase();
  };

  const findColumnKey = (row: any, aliases: string[]) => {
    const keys = Object.keys(row);

    const normalizedAliases = aliases.map(normalizeHeader);

    return keys.find((key) => {
      const normalizedKey = normalizeHeader(key);
      return normalizedAliases.includes(normalizedKey);
    });
  };

  const parseExcelFile = async (file: File) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    const jsonData = XLSX.utils.sheet_to_json<any>(worksheet, {
      defval: "",
    });

    const materialAliases = [
      "اسم المادة",
      "المادة",
      "اسم المنتج",
      "المنتج",
      "Material Name",
      "materialName",
      "Item Name",
      "itemName",
      "Product Name",
      "productName",
      "name",
    ];

    const serialAliases = [
      "السيريال",
      "سيريال",
      "الرقم التسلسلي",
      "Serial",
      "serial",
      "Serial Number",
      "serialNumber",
      "SN",
      "S/N",
      "Barcode",
      "barcode",
    ];
const productIdAliases = [
  "product id",
  "Product ID",
  "productId",
  "ProductId",
  "product_id",
  "رقم المنتج",
  "معرف المنتج",
  "id المنتج",
];

const pointAliases = [
  "point",
  "Point",
  "points",
  "Points",
  "النقاط",
  "نقاط",
];
    const rows: ExcelSerialRow[] = [];

    jsonData.forEach((row, index) => {
      const keys = Object.keys(row);

      if (keys.length === 0) return;

      const materialKey = findColumnKey(row, materialAliases) ?? keys[0];
      const serialKey = findColumnKey(row, serialAliases) ?? keys[1];

    const productIdKey = findColumnKey(row, productIdAliases);
const pointKey = findColumnKey(row, pointAliases);

const materialName = String(row[materialKey] ?? "").trim();
const serial = String(row[serialKey] ?? "").trim();
const productId = productIdKey ? String(row[productIdKey] ?? "").trim() : "";
const point = pointKey ? String(row[pointKey] ?? "").trim() : "";

const normalizedSerial = normalizeSerial(serial);

      if (!normalizedSerial) return;

    rows.push({
  materialName,
  serial,
  normalizedSerial,
  productId,
  point,
  sourceRow: index + 2,
});
    });

    return rows;
  };

  const removeDuplicatesBySerial = (rows: ExcelSerialRow[]) => {
    const map = new Map<string, ExcelSerialRow>();
    let duplicates = 0;

    for (const row of rows) {
      if (map.has(row.normalizedSerial)) {
        duplicates++;
        continue;
      }

      map.set(row.normalizedSerial, row);
    }

    return {
      uniqueRows: Array.from(map.values()),
      duplicates,
    };
  };

  const handleCompanyFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      const parsedRows = await parseExcelFile(file);
      const { uniqueRows, duplicates } = removeDuplicatesBySerial(parsedRows);

      setCompanyRows(uniqueRows);
      setCompanyDuplicatesCount(duplicates);
      setCompanyFileName(file.name);

      toast.success(
        `تم قراءة ملف المبيعات: ${uniqueRows.length} سيريال، وتجاهل ${duplicates} مكرر`
      );

      event.target.value = "";
    } catch (error) {
      console.error("Company Excel parse error:", error);
      toast.error("حدث خطأ أثناء قراءة ملف مبيعات الشركة");
    }
  };

  const handleAgentFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      const parsedRows = await parseExcelFile(file);
      const { uniqueRows, duplicates } = removeDuplicatesBySerial(parsedRows);

      setAgentRows(uniqueRows);
      setAgentDuplicatesCount(duplicates);
      setAgentFileName(file.name);

      toast.success(
        `تم قراءة ملف الوكيل: ${uniqueRows.length} سيريال، وتجاهل ${duplicates} مكرر`
      );

      event.target.value = "";
    } catch (error) {
      console.error("Agent Excel parse error:", error);
      toast.error("حدث خطأ أثناء قراءة ملف جرد الوكيل");
    }
  };

  const companySerialMap = useMemo(() => {
    const map = new Map<string, ExcelSerialRow>();

    for (const row of companyRows) {
      map.set(row.normalizedSerial, row);
    }

    return map;
  }, [companyRows]);

  const agentSerialMap = useMemo(() => {
    const map = new Map<string, ExcelSerialRow>();

    for (const row of agentRows) {
      map.set(row.normalizedSerial, row);
    }

    return map;
  }, [agentRows]);

const commonRows = useMemo<CommonSerialRow[]>(() => {
  const result: CommonSerialRow[] = [];

  Array.from(agentSerialMap.entries()).forEach(
    ([normalizedSerial, agentRow]) => {
      const companyRow = companySerialMap.get(normalizedSerial);

      if (!companyRow) return;

   result.push({
  serial: companyRow.serial || agentRow.serial,
  normalizedSerial,
  companyMaterialName: companyRow.materialName,
  agentMaterialName: agentRow.materialName,
  productId: companyRow.productId || agentRow.productId,
  point: companyRow.point || agentRow.point,
  companyRow: companyRow.sourceRow,
  agentRow: agentRow.sourceRow,
});
    }
  );

  return result;
}, [agentSerialMap, companySerialMap]);

const agentOnlyRows = useMemo<AgentOnlySerialRow[]>(() => {
  const result: AgentOnlySerialRow[] = [];

  Array.from(agentSerialMap.entries()).forEach(
    ([normalizedSerial, agentRow]) => {
      if (companySerialMap.has(normalizedSerial)) return;

    result.push({
  serial: agentRow.serial,
  normalizedSerial,
  agentMaterialName: agentRow.materialName,
  productId: agentRow.productId,
  point: agentRow.point,
  agentRow: agentRow.sourceRow,
});
    }
  );

  return result;
}, [agentSerialMap, companySerialMap]);
  const filteredCommonRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return commonRows;

    return commonRows.filter((row) => {
      return (
        row.serial.toLowerCase().includes(q) ||
        row.normalizedSerial.toLowerCase().includes(q) ||
        row.companyMaterialName.toLowerCase().includes(q) ||
        row.agentMaterialName.toLowerCase().includes(q)
      );
    });
  }, [commonRows, searchQuery]);

  const filteredAgentOnlyRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return agentOnlyRows;

    return agentOnlyRows.filter((row) => {
      return (
        row.serial.toLowerCase().includes(q) ||
        row.normalizedSerial.toLowerCase().includes(q) ||
        row.agentMaterialName.toLowerCase().includes(q)
      );
    });
  }, [agentOnlyRows, searchQuery]);

  const exportCommonRows = () => {
    if (commonRows.length === 0) {
      toast.error("لا توجد سيريلات مشتركة للتصدير");
      return;
    }
const exportData = commonRows.map((row, index) => ({
  "#": index + 1,
  "product id": row.productId,
  "point": row.point,
  "اسم المادة في المبيعات": row.companyMaterialName,
  "اسم المادة في جرد الوكيل": row.agentMaterialName,
  "Serial": row.serial,
  "Normalized Serial": row.normalizedSerial,
  "صف ملف المبيعات": row.companyRow,
  "صف ملف الوكيل": row.agentRow,
  "Status": "exists_in_both_files",
}));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Common Serials");

    XLSX.writeFile(workbook, "serials_exists_in_both_files.xlsx");
  };

  const exportAgentOnlyRows = () => {
    if (agentOnlyRows.length === 0) {
      toast.error("لا توجد سيريلات معزولة للتصدير");
      return;
    }

   const exportData = agentOnlyRows.map((row, index) => ({
  "#": index + 1,
  "product id": row.productId,
  "point": row.point,
  "اسم المادة": row.agentMaterialName,
  "Serial": row.serial,
  "Normalized Serial": row.normalizedSerial,
  "صف ملف الوكيل": row.agentRow,
  "Status": "exists_in_agent_file_only",
}));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Agent Only Serials");

    XLSX.writeFile(workbook, "serials_exists_in_agent_only.xlsx");
  };

  const exportAllResults = () => {
    if (commonRows.length === 0 && agentOnlyRows.length === 0) {
      toast.error("لا توجد نتائج للتصدير");
      return;
    }

    const workbook = XLSX.utils.book_new();

 const commonExportData = commonRows.map((row, index) => ({
  "#": index + 1,
  "product id": row.productId,
  "point": row.point,
  "اسم المادة في المبيعات": row.companyMaterialName,
  "اسم المادة في جرد الوكيل": row.agentMaterialName,
  "Serial": row.serial,
  "Normalized Serial": row.normalizedSerial,
  "صف ملف المبيعات": row.companyRow,
  "صف ملف الوكيل": row.agentRow,
  "Status": "exists_in_both_files",
}));

 const agentOnlyExportData = agentOnlyRows.map((row, index) => ({
  "#": index + 1,
  "product id": row.productId,
  "point": row.point,
  "اسم المادة": row.agentMaterialName,
  "Serial": row.serial,
  "Normalized Serial": row.normalizedSerial,
  "صف ملف الوكيل": row.agentRow,
  "Status": "exists_in_agent_file_only",
}));

    const commonSheet = XLSX.utils.json_to_sheet(commonExportData);
    const agentOnlySheet = XLSX.utils.json_to_sheet(agentOnlyExportData);

    XLSX.utils.book_append_sheet(workbook, commonSheet, "Common Serials");
    XLSX.utils.book_append_sheet(workbook, agentOnlySheet, "Agent Only");

    XLSX.writeFile(workbook, "serials_comparison_results.xlsx");
  };

  const handleReset = () => {
    setCompanyFileName("");
    setAgentFileName("");
    setCompanyRows([]);
    setAgentRows([]);
    setCompanyDuplicatesCount(0);
    setAgentDuplicatesCount(0);
    setSearchQuery("");
    setActiveTab("common");
  };

  const hasBothFiles = companyRows.length > 0 && agentRows.length > 0;

  return (
    <div
      className="w-full max-w-none mx-auto bg-white rounded-xl shadow-lg"
      style={{ width: 1400, direction: "rtl" }}
    >
      <div className="max-w-full flex items-center justify-between bg-[#262d34] text-white px-6 py-3 rounded-t-lg shadow-md">
        <h1
          className="text-xl font-bold flex-1 text-right"
          style={{ fontFamily: "Tajawal, sans-serif" }}
        >
          مقارنة سيريلات المبيعات مع جرد الوكلاء
        </h1>

        <div className="flex items-center gap-3">
          <label>
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleCompanyFileUpload}
            />
            <Button
              variant="contained"
              component="span"
              sx={{
                fontFamily: "Tajawal, sans-serif",
                backgroundColor: "#0f766e",
              }}
              startIcon={<FontAwesomeIcon icon={faUpload} />}
            >
              ملف مبيعات الشركة
            </Button>
          </label>

          <label>
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleAgentFileUpload}
            />
            <Button
              variant="contained"
              component="span"
              sx={{
                fontFamily: "Tajawal, sans-serif",
                backgroundColor: "#2563eb",
              }}
              startIcon={<FontAwesomeIcon icon={faUpload} />}
            >
              ملف جرد الوكيل
            </Button>
          </label>

          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              fontFamily: "Tajawal, sans-serif",
              color: "#fff",
              borderColor: "#fff",
            }}
            startIcon={<FontAwesomeIcon icon={faRotateRight} />}
          >
            إعادة تعيين
          </Button>
        </div>
      </div>

      <div
        style={{
          padding: "16px 24px",
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(180px, 1fr))",
          gap: 14,
          fontFamily: "Tajawal, sans-serif",
        }}
      >
        <StatCard
          title="سيريلات ملف المبيعات"
          value={companyRows.length}
          color="#0f766e"
          subTitle={companyFileName || "لم يتم اختيار ملف"}
        />

        <StatCard
          title="سيريلات ملف الوكيل"
          value={agentRows.length}
          color="#2563eb"
          subTitle={agentFileName || "لم يتم اختيار ملف"}
        />

        <StatCard
          title="الموجودة في كلا الملفين"
          value={commonRows.length}
          color="#7c3aed"
          subTitle="بدون تكرار"
        />

        <StatCard
          title="موجودة عند الوكيل فقط"
          value={agentOnlyRows.length}
          color="#dc2626"
          subTitle="غير موجودة في المبيعات"
        />

        <StatCard
          title="التكرارات المتجاهلة"
          value={companyDuplicatesCount + agentDuplicatesCount}
          color="#ea580c"
          subTitle={`مبيعات: ${companyDuplicatesCount} | وكيل: ${agentDuplicatesCount}`}
        />
      </div>

      <div
        style={{
          padding: "0 24px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          fontFamily: "Tajawal, sans-serif",
        }}
      >
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            variant={activeTab === "common" ? "contained" : "outlined"}
            onClick={() => setActiveTab("common")}
            sx={{ fontFamily: "Tajawal, sans-serif" }}
          >
            السيريالات المشتركة
          </Button>

          <Button
            variant={activeTab === "agentOnly" ? "contained" : "outlined"}
            onClick={() => setActiveTab("agentOnly")}
            color="error"
            sx={{ fontFamily: "Tajawal, sans-serif" }}
          >
            سيريالات الوكيل غير الموجودة في المبيعات
          </Button>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="flex items-center bg-gray-100 rounded-full px-4 py-1 w-80 shadow-md">
            <FontAwesomeIcon icon={faSearch} className="text-gray-500 ml-2" />
            <input
              type="text"
              placeholder="البحث باسم المادة أو السيريال..."
              className="bg-transparent focus:outline-none w-full text-sm text-gray-700 py-1"
              style={{
                fontFamily: "Tajawal, sans-serif",
                textAlign: "right",
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Button
            variant="contained"
            color="success"
            onClick={exportCommonRows}
            disabled={!hasBothFiles || commonRows.length === 0}
            sx={{ fontFamily: "Tajawal, sans-serif" }}
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            تصدير المشتركة
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={exportAgentOnlyRows}
            disabled={!hasBothFiles || agentOnlyRows.length === 0}
            sx={{ fontFamily: "Tajawal, sans-serif" }}
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            تصدير المعزولة
          </Button>

          <Button
            variant="contained"
            onClick={exportAllResults}
            disabled={!hasBothFiles}
            sx={{
              fontFamily: "Tajawal, sans-serif",
              backgroundColor: "#262d34",
            }}
            startIcon={<FontAwesomeIcon icon={faDownload} />}
          >
            تصدير الكل
          </Button>
        </div>
      </div>

      {!hasBothFiles ? (
        <div
          style={{
            padding: 30,
            textAlign: "center",
            fontFamily: "Tajawal, sans-serif",
            color: "#64748b",
            fontSize: 16,
          }}
        >
          يرجى اختيار ملف مبيعات الشركة وملف جرد الوكيل حتى يتم تنفيذ المقارنة.
        </div>
      ) : (
        <div className="table-container" style={{ overflowX: "auto" }}>
          {activeTab === "common" ? (
            <table className="product-table" style={{ width: "100%" }}>
              <thead className="color-table">
                <tr>
                  <th>#</th>
                  <th>اسم المادة في المبيعات</th>
                  <th>اسم المادة في جرد الوكيل</th>
                  <th>Serial</th>
                  <th>Normalized Serial</th>
                  <th>صف المبيعات</th>
                  <th>صف الوكيل</th>
                  <th>الحالة</th>
                </tr>
              </thead>

              <tbody>
                {filteredCommonRows.map((row, index) => (
                  <tr key={row.normalizedSerial}>
                    <td>{index + 1}</td>
                    <td>{row.companyMaterialName || "-"}</td>
                    <td>{row.agentMaterialName || "-"}</td>
                    <td style={{ direction: "ltr" }}>{row.serial}</td>
                    <td style={{ direction: "ltr" }}>{row.normalizedSerial}</td>
                    <td>{row.companyRow}</td>
                    <td>{row.agentRow}</td>
                    <td>
                      <span
                        style={{
                          background: "#ede9fe",
                          color: "#6d28d9",
                          padding: "5px 10px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: "bold",
                        }}
                      >
                        موجود في كلا الملفين
                      </span>
                    </td>
                  </tr>
                ))}

                {filteredCommonRows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: 20 }}>
                      لا توجد بيانات
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="product-table" style={{ width: "100%" }}>
              <thead className="color-table">
                <tr>
                  <th>#</th>
                  <th>اسم المادة</th>
                  <th>Serial</th>
                  <th>Normalized Serial</th>
                  <th>صف ملف الوكيل</th>
                  <th>الحالة</th>
                </tr>
              </thead>

              <tbody>
                {filteredAgentOnlyRows.map((row, index) => (
                  <tr key={row.normalizedSerial}>
                    <td>{index + 1}</td>
                    <td>{row.agentMaterialName || "-"}</td>
                    <td style={{ direction: "ltr" }}>{row.serial}</td>
                    <td style={{ direction: "ltr" }}>{row.normalizedSerial}</td>
                    <td>{row.agentRow}</td>
                    <td>
                      <span
                        style={{
                          background: "#fee2e2",
                          color: "#b91c1c",
                          padding: "5px 10px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: "bold",
                        }}
                      >
                        موجود عند الوكيل فقط
                      </span>
                    </td>
                  </tr>
                ))}

                {filteredAgentOnlyRows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: 20 }}>
                      لا توجد سيريلات معزولة
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{
  title: string;
  value: number;
  color: string;
  subTitle?: string;
}> = ({ title, value, color, subTitle }) => {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "14px 18px",
        textAlign: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        minHeight: 105,
      }}
    >
      <div style={{ fontSize: 14, color: "#64748b", marginBottom: 6 }}>
        {title}
      </div>

      <div style={{ fontSize: 28, fontWeight: "bold", color }}>{value}</div>

      {subTitle && (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            marginTop: 5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={subTitle}
        >
          {subTitle}
        </div>
      )}
    </div>
  );
};

export default SerialComparison;